import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, moneyCompact, STATUS_LABELS } from "@/lib/format";
import { X, Building2, History, MessageSquare, Save, GitMerge, Trash2, Activity, Clock, Mail, Reply, Search, ChevronDown, ChevronRight, ArrowLeft, Plus } from "lucide-react";
import CommentThread from "@/components/CommentThread";
import { EngagementChip } from "@/components/EngagementChip";
import { AddContactDialog } from "@/components/ClientContactPicker";
import ContextTimeline from "@/components/ContextTimeline";
import { useAuth } from "@/context/AuthContext";
import { useSyncedRefresh } from "@/hooks/useLiveSync";
import { useLocation } from "react-router-dom";
import ClientDocuments from "@/components/nda/ClientDocuments";
import { NdaBanner, NdaMark } from "@/components/nda/NdaBits";

const BRIEF_MAX = 10000;

const fmtHrs = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h < 10 ? h.toFixed(1) : Math.round(h)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

const FIELDS = [
  ["contact_name", "Primary contact"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"],
  ["rocketfuel_url", "RocketFuel account link"], ["folder_name", "Folder name (Google Drive)"],
];

export default function ClientDetail({ name, onClose, onMerged, asPage = false }) {
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [bids, setBids] = useState([]);
  const [audit, setAudit] = useState([]);
  const [profile, setProfile] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({});
  const location = useLocation();
  const [tab, setTab] = useState(() => (new URLSearchParams(location.search).get("tab") === "documents" ? "documents" : "context"));
  const [nda, setNda] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [mergeTarget, setMergeTarget] = useState("");
  const [rollup, setRollup] = useState(null);
  const [projFilter, setProjFilter] = useState("");
  const [replyState, setReplyState] = useState(null);
  const [search, setSearch] = useState("");
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [rollupView, setRollupView] = useState("projects");   // "projects" (grouped, inline) | "stream" (unified)
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const groupsInit = React.useRef(false);
  const canMerge = ["owner", "admin", "chief_estimator", "business_development"].includes(user?.role);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await api.get("/client-resolve", { params: { name } });
        const cid = res.data.client_id;
        const [detail, au] = await Promise.all([
          api.get(`/client-detail/${cid}`),
          api.get("/audit", { params: { entity_type: "client", entity_id: cid } }),
        ]);
        if (ignore) return;
        setClient(detail.data.client); setBids(detail.data.bids);
        api.get(`/clients/${cid}/compliance`).then((r) => { if (!ignore) setNda(r.data.summary); }).catch(() => {});
        setProfile(detail.data.profile || null); setContacts(detail.data.contacts || []);
        setReplyState(detail.data.reply_state || null);
        setForm({ ...detail.data.client, tags: (detail.data.client.tags || []).join(", ") });
        setAudit(au.data);
      } catch { if (!ignore) toast.error("Could not load client"); }
    })();
    return () => { ignore = true; };
  }, [name]);

  const refreshContacts = () => {
    if (!client?.name) return;
    api.get("/client-contacts", { params: { name: client.name } }).then((r) => setContacts(r.data || [])).catch(() => {});
  };

  const save = async () => {
    if (!client) return; setSaving(true);
    try {
      const body = { ...form, tags: String(form.tags || "").split(",").map((t) => t.trim()).filter(Boolean) };
      delete body.client_id; delete body.created_at; delete body.updated_at;
      const r = await api.put(`/clients/${client.client_id}`, body);
      setClient(r.data); toast.success("Client saved");
      api.get("/audit", { params: { entity_type: "client", entity_id: client.client_id } }).then((x) => setAudit(x.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 400;
        let { width, height } = img;
        if (width > max || height > max) { const s = Math.min(max / width, max / height); width = Math.round(width * s); height = Math.round(height * s); }
        const c = document.createElement("canvas"); c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        set("logo", c.toDataURL("image/png"));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const doMerge = async () => {
    if (!mergeTarget || !client) return;
    if (!window.confirm(`Merge “${client.name}” into another client? Its bids, comments and history move over, and “${client.name}” is removed.`)) return;
    try {
      await api.post("/clients/merge", { source_id: client.client_id, target_id: mergeTarget });
      toast.success("Clients merged");
      onMerged?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not merge"); }
  };
  const doDelete = async () => {
    if (!client) return;
    const linked = bids.length;
    const warn = linked
      ? `Remove “${client.name}” from the client list? Its ${linked} bid${linked === 1 ? "" : "s"} stay but are unlinked. This can't be undone.`
      : `Remove “${client.name}” from the client list? This can't be undone.`;
    if (!window.confirm(warn)) return;
    try {
      await api.delete(`/clients/${client.client_id}`);
      toast.success("Client removed");
      onMerged?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove client"); }
  };
  useEffect(() => { if (canMerge) api.get("/client-merge-options").then((r) => setAllClients(r.data)).catch(() => {}); }, [canMerge]);
  useEffect(() => {
    if (tab === "timeline" && client && !rollup) {
      api.get(`/client-context/${client.client_id}`).then((r) => setRollup(r.data)).catch(() => toast.error("Could not load conversation"));
    }
  }, [tab, client, rollup]);
  // Live-refresh the open conversation the moment new mail lands.
  useSyncedRefresh(() => {
    if (tab === "timeline" && client) {
      api.get(`/client-context/${client.client_id}`).then((r) => setRollup(r.data)).catch(() => {});
    }
  });

  const matchSearch = React.useCallback((i) => {
    const q = search.trim().toLowerCase();
    return !q || [i.subject, i.summary, i.body, i.counterparty, i.sender_name, i.sender_company, i.project, i.text, i.note, i.author, i.owner, i.label, i.kind]
      .filter(Boolean).join(" ").toLowerCase().includes(q);
  }, [search]);

  const filteredItems = React.useMemo(() => {
    if (!rollup) return [];
    let items = rollup.items;
    if (projFilter === "__unassigned") items = items.filter((i) => i.type === "email" && i.unassigned);
    else if (projFilter) {
      const selName = rollup.projects.find((p) => p.bid_id === projFilter)?.name;
      items = items.filter((i) => (i.type === "email" ? i.bid_id === projFilter : i.project === selName));
    }
    return items.filter(matchSearch);
  }, [rollup, projFilter, matchSearch]);

  // Group every item by the project it belongs to (emails by bound bid, activities/comments/events by
  // project name), so each pursuit's whole conversation opens inline without leaving the client view.
  const projectGroups = React.useMemo(() => {
    if (!rollup) return [];
    const byKey = {};
    rollup.items.forEach((it) => {
      let key;
      if (it.type === "email") key = (it.unassigned || !it.bid_id) ? "__unassigned" : it.bid_id;
      else {
        const p = it.project ? rollup.projects.find((x) => x.name === it.project) : null;
        key = p ? p.bid_id : (it.project ? `name:${it.project}` : "__unassigned");
      }
      (byKey[key] ||= []).push(it);
    });
    const mk = (key, name, extra) => ({
      key, name,
      items: (byKey[key] || []).filter(matchSearch).sort((a, b) => ((a.date || "") < (b.date || "") ? 1 : -1)),
      ...extra,
    });
    const groups = rollup.projects.map((p) => mk(p.bid_id, p.name, { status: p.status, stored_total: p.stored_total, capacity_mw: p.capacity_mw }));
    Object.keys(byKey).filter((k) => k.startsWith("name:")).forEach((k) => groups.push(mk(k, k.slice(5), {})));
    groups.push(mk("__unassigned", "Unassigned", { unassigned: true }));
    return groups.filter((g) => g.items.length);
  }, [rollup, matchSearch]);

  const toggleGroup = (k) => setOpenGroups((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  // Open the first project's conversation by default so the client view lands on live context.
  useEffect(() => {
    if (rollup && !groupsInit.current && projectGroups.length) {
      groupsInit.current = true;
      setOpenGroups(new Set([projectGroups[0].key]));
    }
  }, [rollup, projectGroups]);

  const inner = (
    <>
      <div className={`sticky top-0 bg-white border-b border-slate-100 ${asPage ? "px-6" : "px-5"} py-3 flex items-center ${asPage ? "gap-3" : "justify-between"} z-10`}>
        <div className="flex items-center gap-2 min-w-0">
          {asPage && (
            <button onClick={onClose} data-testid="client-back" className="text-slate-500 hover:text-black mr-1 shrink-0 inline-flex items-center gap-1 text-sm font-medium">
              <ArrowLeft className="w-4 h-4" /> Clients
            </button>
          )}
          <Building2 className="w-4 h-4 text-[#003E87] shrink-0" />
          <div className="font-display text-base font-bold text-black truncate">{name}</div>
          {nda && <NdaMark state={nda.state} label testid="client-header-nda" />}
        </div>
        {!asPage && <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="client-detail-close"><X className="w-4 h-4" /></button>}
      </div>

        <div className="flex gap-1 px-5 pt-3 border-b border-slate-100">
          {[["context", "Context"], ["documents", "Documents"], ["timeline", "Conversation"], ["profile", "Engagement"], ["comments", "Comments"], ["history", "History"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`client-tab-${k}`}
              className={`text-sm px-3 py-2 border-b-2 -mb-px flex items-center gap-1.5 ${tab === k ? "border-[#F4C400] text-black font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {l}
              {k === "documents" && nda && ["none", "sent", "expired"].includes(nda.state) && (
                <span data-testid="client-tab-documents-ping" title="No active NDA" className="w-2 h-2 rounded-full bg-red-500 ring-2 ring-red-500/30" />
              )}
              {k === "timeline" && replyState?.owe && (
                <span data-testid="client-tab-timeline-ping" title="A project owes a reply"
                  className="w-2 h-2 rounded-full bg-[#F4C400] ring-2 ring-[#F4C400]/30" />
              )}
            </button>
          ))}
        </div>

        {!client ? <div className="p-8 text-sm text-slate-400">Loading…</div> : (
          <div className="p-5">
            {tab !== "documents" && nda && <div className="mb-4"><NdaBanner summary={nda} what={client.name} testid="client-nda-banner-top" /></div>}
            {tab === "documents" && <ClientDocuments client={client} key={client.client_id} onCompliance={setNda} />}
            {tab === "context" && (
              <div className="space-y-4" data-testid="client-context">
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {FIELDS.map(([f, label]) => (
                    <div key={f}>
                      <label className="text-[11px] uppercase tracking-wide text-slate-400">{label}</label>
                      <input value={form[f] || ""} onChange={(e) => set(f, e.target.value)} className="w-full text-sm border border-slate-200 rounded px-3 py-2 mt-1" data-testid={`client-field-${f}`} />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 -mt-2" data-testid="client-folder-name-hint">Folder name is the short name (no spaces, e.g. Sun-Tribe) used for this client's Google Drive folders. Changing it renames the existing folders; links keep working.</p>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-slate-400">Client logo <span className="text-slate-300 normal-case">— shown on the estimate PDF cover</span></label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.logo
                      ? <img src={form.logo} alt="client logo" data-testid="client-logo-preview" className="h-12 w-auto max-w-[160px] object-contain border border-slate-200 rounded bg-white p-1" />
                      : <div className="h-12 w-24 grid place-items-center text-[10px] text-slate-400 border border-dashed border-slate-200 rounded">No logo</div>}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs px-3 py-1.5 rounded border border-slate-200 cursor-pointer hover:bg-slate-50 inline-block w-fit">
                        Upload<input type="file" accept="image/*" className="hidden" data-testid="client-logo-input" onChange={onLogo} />
                      </label>
                      {form.logo && <button onClick={() => set("logo", "")} data-testid="client-logo-clear" className="text-xs text-rose-500 hover:underline text-left">Remove</button>}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-slate-400">Tags (comma-separated)</label>
                  <input value={form.tags || ""} onChange={(e) => set("tags", e.target.value)} placeholder="EPC, Texas, Net-30" className="w-full text-sm border border-slate-200 rounded px-3 py-2 mt-1" data-testid="client-field-tags" />
                </div>
                <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="client-brief">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Client brief <span className="font-normal normal-case text-slate-400">· owned by BD</span></span>
                    {client.brief?.written_by && <span className="text-[11px] text-slate-400" data-testid="client-brief-meta">Last written by {client.brief.written_by}{client.brief.updated_at ? ` · ${new Date(client.brief.updated_at).toLocaleDateString()}` : ""}</span>}
                  </div>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {[["relationship", "Relationship"], ["history", "History"], ["standing", "Standing"], ["cares_about", "What they care about"], ["open_paperwork", "Open paperwork"], ["next_touch", "Next touch"]].map(([k, l]) => {
                      const n = (form.brief?.[k] || "").length, warn = n > BRIEF_MAX * 0.9;
                      return (
                      <div key={k}>
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] uppercase tracking-wide text-slate-400">{l}</label>
                          <span className={`text-[10px] tabular-nums ${n > BRIEF_MAX ? "text-rose-600 font-semibold" : warn ? "text-amber-600" : "text-slate-400"}`} data-testid={`client-brief-${k}-count`}>{n.toLocaleString()} / {BRIEF_MAX.toLocaleString()}</span>
                        </div>
                        <textarea value={form.brief?.[k] || ""} onChange={(e) => setForm({ ...form, brief: { ...(form.brief || {}), [k]: e.target.value } })} rows={2}
                          className={`w-full text-sm border rounded px-3 py-2 mt-1 resize-none ${n > BRIEF_MAX ? "border-rose-300" : "border-slate-200"}`} data-testid={`client-brief-${k}`} />
                        {n > BRIEF_MAX && <p className="text-[11px] text-rose-600 mt-0.5">Over the limit by {(n - BRIEF_MAX).toLocaleString()} characters; the rest is cut on save. Split it across fields.</p>}
                      </div>
                      );
                    })}
                  </div>
                  {form.notes && <p className="text-[11px] text-slate-400">Earlier notes: {form.notes}</p>}
                </div>
                <button onClick={save} disabled={saving} className="text-sm px-4 py-2 rounded bg-[#F4C400] text-black font-medium flex items-center gap-1.5" data-testid="client-save"><Save className="w-4 h-4" /> Save</button>

                {canMerge && (
                  <div className="pt-4 border-t border-slate-100" data-testid="client-delete-section">
                    <button onClick={doDelete} data-testid="client-delete"
                      className="text-sm px-3 py-2 rounded border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center gap-1.5">
                      <Trash2 className="w-4 h-4" /> Remove client
                    </button>
                    <p className="text-[11px] text-slate-400 mt-1.5">Deletes this client from the list. Linked bids stay but are unlinked.</p>
                  </div>
                )}

                {canMerge && allClients.length > 1 && (
                  <div className="pt-4 border-t border-slate-100" data-testid="client-merge">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><GitMerge className="w-3.5 h-3.5" /> Merge duplicate</div>
                    <div className="flex gap-2">
                      <select value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} className="flex-1 text-sm border border-slate-200 rounded px-2 py-2" data-testid="merge-target">
                        <option value="">Merge this client into…</option>
                        {allClients.filter((c) => c.client_id !== client.client_id).map((c) => <option key={c.client_id} value={c.client_id}>{c.name} · {c.bids} bid{c.bids === 1 ? "" : "s"} · {c.comments} comment{c.comments === 1 ? "" : "s"}</option>)}
                      </select>
                      <button onClick={doMerge} disabled={!mergeTarget} className="text-sm px-3 py-2 rounded border border-slate-200 text-slate-700 disabled:opacity-40" data-testid="merge-btn">Merge</button>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-slate-100">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Pursuits ({bids.length})</div>
                  <div className="space-y-1.5 max-h-56 overflow-auto">
                    {bids.length === 0 && <div className="text-xs text-slate-400">No bids linked to this client yet.</div>}
                    {bids.map((b) => (
                      <div key={b.bid_id} className="flex items-center justify-between text-xs border border-slate-100 rounded px-3 py-1.5" data-testid={`client-bid-${b.bid_id}`}>
                        <div className="min-w-0"><div className="text-slate-700 truncate">{b.name}</div><div className="text-slate-400">{STATUS_LABELS[b.status] || b.status}{b.state ? ` · ${b.state}` : ""}{b.capacity_mw ? ` · ${b.capacity_mw} MW` : ""}</div></div>
                        {b.stored_total ? <div className="font-mono text-slate-700 shrink-0">{moneyCompact(b.stored_total)}</div> : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {tab === "timeline" && (
              <div data-testid="client-rollup">
                {!rollup ? <div className="py-10 text-sm text-slate-400 text-center">Loading conversation…</div> : (
                  <>
                    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${rollup.reply_state.owe ? "bg-[#F4C400]/20 text-[#8a6d00]" : rollup.reply_state.last_outbound_at ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-500"}`} data-testid="client-rollup-reply-state">
                        {rollup.reply_state.owe ? "You owe a reply" : (rollup.reply_state.last_outbound_at ? "Waiting on client" : "No email yet")}
                      </span>
                      <span className="text-[11px] text-slate-400">{rollup.counts.emails} email{rollup.counts.emails === 1 ? "" : "s"} · {rollup.counts.projects} project{rollup.counts.projects === 1 ? "" : "s"}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3 bg-slate-100 rounded-lg p-0.5 w-fit" data-testid="client-rollup-view">
                      {[["projects", "By project"], ["stream", "All in one stream"]].map(([k, l]) => (
                        <button key={k} onClick={() => setRollupView(k)} data-testid={`client-rollup-view-${k}`}
                          className={`text-xs px-3 py-1 rounded-md transition ${rollupView === k ? "bg-white text-black shadow-sm font-medium" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
                      ))}
                    </div>
                    {rollup.projects.length > 0 && (
                      <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1" data-testid="client-rollup-pursuits">
                        {rollup.projects.map((p) => {
                          const on = projFilter === p.bid_id;
                          return (
                            <button key={p.bid_id} data-testid={`client-rollup-pursuit-${p.bid_id}`}
                              onClick={() => {
                                if (rollupView === "projects") { setOpenGroups(new Set([p.bid_id])); document.querySelector(`[data-testid="client-project-group-${p.bid_id}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }
                                else setProjFilter(on ? "" : p.bid_id);
                              }}
                              className={`shrink-0 text-left rounded-lg border px-3 py-2 min-w-[140px] transition ${on && rollupView === "stream" ? "border-[#003E87] ring-1 ring-[#003E87]/30 bg-[#003E87]/5" : "border-slate-200 hover:border-slate-300"}`}>
                              <div className="text-xs font-medium text-slate-800 truncate max-w-[160px]">{p.name}</div>
                              <div className="font-mono text-sm font-bold text-black tabular-nums mt-0.5">{p.stored_total ? moneyCompact(p.stored_total) : "—"}</div>
                              <div className="text-[10px] text-slate-400">{STATUS_LABELS[p.status] || p.status}{p.capacity_mw ? ` · ${p.capacity_mw} MW` : ""}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="relative mb-3">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="client-rollup-search"
                        placeholder="Search this conversation — keyword or contact"
                        className="w-full text-xs border border-slate-200 rounded pl-8 pr-3 py-2" />
                    </div>
                    {rollupView === "stream" && rollup.projects.length > 1 && (
                      <select value={projFilter} onChange={(e) => setProjFilter(e.target.value)} data-testid="client-rollup-filter"
                        className="w-full text-xs border border-slate-200 rounded px-2 py-2 mb-3 bg-white">
                        <option value="">All projects</option>
                        {rollup.projects.map((p) => <option key={p.bid_id} value={p.bid_id}>{p.name}</option>)}
                        <option value="__unassigned">Unassigned</option>
                      </select>
                    )}
                    {rollupView === "stream" ? (
                      search.trim() && filteredItems.length === 0 ? (
                        <div className="py-10 text-sm text-slate-400 text-center" data-testid="client-rollup-no-results">No messages match “{search.trim()}”.</div>
                      ) : (
                        <ContextTimeline items={filteredItems} />
                      )
                    ) : (
                      projectGroups.length === 0 ? (
                        <div className="py-10 text-sm text-slate-400 text-center" data-testid="client-rollup-no-results">
                          {search.trim() ? `No messages match “${search.trim()}”.` : "No conversation logged yet."}
                        </div>
                      ) : (
                        <div className="space-y-2" data-testid="client-project-groups">
                          {projectGroups.map((g) => {
                            const open = openGroups.has(g.key);
                            const lastEmail = g.items.filter((i) => i.type === "email").sort((a, b) => ((a.date || "") < (b.date || "") ? 1 : -1))[0];
                            const awaiting = lastEmail?.direction === "in";
                            const emailCount = g.items.filter((i) => i.type === "email").length;
                            return (
                              <div key={g.key} data-testid={`client-project-group-${g.key}`} className="border border-slate-200 rounded-lg overflow-hidden">
                                <button data-testid={`client-project-group-toggle-${g.key}`} onClick={() => toggleGroup(g.key)}
                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50">
                                  {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5">
                                      {g.name}
                                      {awaiting && <span title="They replied last — you owe a response" data-testid={`client-project-owe-${g.key}`} className="w-2 h-2 rounded-full bg-[#F4C400] shrink-0" />}
                                    </div>
                                    <div className="text-[11px] text-slate-400">
                                      {g.status ? `${STATUS_LABELS[g.status] || g.status} · ` : ""}{emailCount} email{emailCount === 1 ? "" : "s"}{g.items.length > emailCount ? ` · ${g.items.length - emailCount} note${g.items.length - emailCount === 1 ? "" : "s"}/activity` : ""}{g.capacity_mw ? ` · ${g.capacity_mw} MW` : ""}
                                    </div>
                                  </div>
                                  {g.stored_total ? <div className="font-mono text-xs text-slate-700 shrink-0">{moneyCompact(g.stored_total)}</div> : null}
                                </button>
                                {open && (
                                  <div className="border-t border-slate-100 px-2 py-2 bg-slate-50/40" data-testid={`client-project-conversation-${g.key}`}>
                                    <ContextTimeline items={g.items} />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            )}
            {tab === "profile" && (
              <div className="space-y-5" data-testid="client-profile">
                {!profile || profile.touches === 0 ? (
                  <div className="text-sm text-slate-400 py-8 text-center">
                    <Mail className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                    No email activity recorded with this client yet. As emails sync, the responsiveness profile builds here.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <EngagementChip label={profile.label} replyRate={profile.reply_rate_pct} testid="profile-engagement" />
                      <span className="text-[11px] text-slate-400">{profile.touches} email{profile.touches === 1 ? "" : "s"} · {profile.threads} thread{profile.threads === 1 ? "" : "s"}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border border-slate-100 p-3" data-testid="profile-reply-rate">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Reply className="w-3 h-3" /> Reply rate</div>
                        <div className="font-display text-xl font-bold text-black mt-1">{profile.reply_rate_pct != null ? `${profile.reply_rate_pct}%` : "—"}</div>
                        <div className="text-[11px] text-slate-400">{profile.responded} answered · {profile.unanswered} ignored</div>
                      </div>
                      <div className="rounded-lg border border-slate-100 p-3" data-testid="profile-response-time">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Typical reply</div>
                        <div className="font-display text-xl font-bold text-black mt-1">{fmtHrs(profile.median_response_hours)}</div>
                        <div className="text-[11px] text-slate-400">first reply {fmtHrs(profile.median_first_response_hours)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div><span className="text-slate-400">We sent</span><div className="font-medium text-slate-700">{profile.outbound}</div></div>
                      <div><span className="text-slate-400">They sent</span><div className="font-medium text-slate-700">{profile.inbound}</div></div>
                      <div><span className="text-slate-400">Last from them</span><div className="font-medium text-slate-700">{profile.last_inbound_at ? fmtDate(profile.last_inbound_at) : "—"}</div></div>
                      <div><span className="text-slate-400">Last from us</span><div className="font-medium text-slate-700">{profile.last_outbound_at ? fmtDate(profile.last_outbound_at) : "—"}</div></div>
                    </div>
                    {profile.silence_days != null && (
                      <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">Last contact {profile.silence_days}d ago.{profile.best_reply ? ` They usually reply ${profile.best_reply.label}.` : ""}</div>
                    )}
                  </>
                )}
                {/* People at this client — ALWAYS available; contacts pre-date the first email. */}
                <div className="border-t border-slate-100 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> People at this client</div>
                        <button data-testid="client-add-contact" onClick={() => setAddContactOpen(true)} className="inline-flex items-center gap-1 text-[11px] font-medium text-[#003E87] hover:underline"><Plus className="w-3.5 h-3.5" /> Add contact</button>
                      </div>
                      {contacts.length === 0 ? (
                        <div className="text-xs text-slate-400">No contacts yet. Add one so you can reuse it on new bids and leads.</div>
                      ) : (
                        <div className="space-y-2">
                          {contacts.map((c) => (
                            <div key={c.contact_id || c.email} className="flex items-center justify-between gap-2 border border-slate-100 rounded px-3 py-2" data-testid={`profile-contact-${c.email || c.contact_id}`}>
                              <div className="min-w-0">
                                <div className="text-sm text-slate-700 truncate">{c.name || c.email}{c.role ? <span className="text-slate-400 font-normal"> · {c.role}</span> : null}</div>
                                <div className="text-[11px] text-slate-400 truncate">{c.email || "no email"}{c.touches ? ` · ${c.touches} email${c.touches === 1 ? "" : "s"} · replies ${fmtHrs(c.median_response_hours)}${c.best_reply ? ` · usually ${c.best_reply.label}` : ""}` : (c.source === "saved" ? " · saved contact" : "")}</div>
                              </div>
                              {c.label ? <EngagementChip label={c.label} replyRate={c.reply_rate_pct} /> : (c.source === "saved" ? <span className="text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 shrink-0">Saved</span> : null)}
                            </div>
                          ))}
                        </div>
                      )}
                      <AddContactDialog open={addContactOpen} onOpenChange={setAddContactOpen} clientName={client?.name} onCreated={refreshContacts} />
                    </div>
              </div>
            )}
            {tab === "comments" && <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-400 mb-3"><MessageSquare className="w-3.5 h-3.5" /> Team comments</div>}
            {tab === "comments" && <CommentThread entityType="client" entityId={client.client_id} />}
            {tab === "history" && (
              <div data-testid="client-history">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-400 mb-3"><History className="w-3.5 h-3.5" /> Change log</div>
                {audit.length === 0 && <div className="text-xs text-slate-400">No edits recorded yet.</div>}
                <div className="space-y-2">
                  {audit.map((a) => (
                    <div key={a.id} className="text-xs flex gap-2" data-testid={`audit-${a.id}`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#003E87] mt-1.5 shrink-0" />
                      <div>
                        <span className="text-slate-700"><b>{a.field}</b> changed {a.old ? <>from “{String(a.old)}” </> : ""}to “{String(a.new)}”</span>
                        <span className="text-slate-400"> — {a.actor_name}, {fmtDate(a.at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
    </>
  );

  return asPage ? (
    <div className="bg-white min-h-full" data-testid="client-detail">
      <div className="max-w-[1800px] mx-auto w-full">{inner}</div>
    </div>
  ) : (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="client-detail" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        {inner}
      </div>
    </div>
  );
}
